import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import logoImage from '../../assets/images/laconia-logo-white.png';

const HeaderContainer = styled.header`
  background-color: #000;
  box-shadow: 0 1px 0 rgba(255, 255, 255, 0.1);
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;
`;

const Nav = styled.nav`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 5%;
  max-width: 1200px;
  margin: 0 auto;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const LogoContainer = styled(Link)`
  display: flex;
  align-items: center;
  text-decoration: none;
  margin-right: 2rem;
`;

const LogoImage = styled.img`
  height: 40px;
  width: auto;
  /* No filter needed as logo is already white */
`;

const MenuItems = styled.ul`
  display: flex;
  list-style: none;
  margin: 0;
  padding: 0;
  transition: all 0.3s ease;

  @media (max-width: 768px) {
    flex-direction: column;
    width: 100%;
    max-height: ${({ isOpen }) => (isOpen ? '300px' : '0')};
    overflow: hidden;
    background-color: #000;
    padding: ${({ isOpen }) => (isOpen ? '1rem 0' : '0')};
  }
`;

const MenuItem = styled.li`
  margin: 0 1.25rem;
  
  @media (max-width: 768px) {
    margin: 1rem 0;
    width: 100%;
  }
`;

const NavLink = styled(Link)`
  color: #fff;
  text-decoration: none;
  font-weight: 500;
  transition: color 0.2s ease;
  padding: 0.5rem 0;
  position: relative;
  margin: 0 0.75rem;
  
  &:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: ${props => props.active ? '100%' : '0'};
    height: 2px;
    background-color: #880000;
    transition: width 0.3s ease;
  }
  
  &:hover {
    color: #880000;
  }
  
  &:hover:after {
    width: 100%;
  }
  
  &:focus-visible {
    outline: 2px solid #880000;
    outline-offset: 4px;
  }
`;

const MenuToggle = styled.button`
  display: none;
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #fff;
  cursor: pointer;
  padding: 0.5rem;
  z-index: 100;
  
  &:focus-visible {
    outline: 2px solid #880000;
    outline-offset: 2px;
  }
  
  @media (max-width: 768px) {
    display: block;
    position: absolute;
    top: 1rem;
    right: 5%;
  }
`;

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  
  // Close mobile menu when route changes
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location]);
  
  // Close menu when clicking outside on mobile
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (isMenuOpen && !e.target.closest('nav')) {
        setIsMenuOpen(false);
      }
    };
    
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [isMenuOpen]);
  
  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  return (
    <HeaderContainer>
      <Nav>
        <LogoContainer to="/">
          <LogoImage src={logoImage} alt="Laconia" />
        </LogoContainer>
        <MenuToggle 
          onClick={toggleMenu}
          aria-expanded={isMenuOpen}
          aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
        >
          {isMenuOpen ? '✕' : '☰'}
        </MenuToggle>
        <MenuItems isOpen={isMenuOpen}>
          <MenuItem>
            <NavLink to="/" active={location.pathname === '/'}>Home</NavLink>
          </MenuItem>
          <MenuItem>
            <NavLink to="/services" active={location.pathname === '/services'}>Services</NavLink>
          </MenuItem>
          <MenuItem>
            <NavLink to="/about" active={location.pathname === '/about'}>About</NavLink>
          </MenuItem>
          <MenuItem>
            <NavLink to="/blog" active={location.pathname.startsWith('/blog')}>Insights</NavLink>
          </MenuItem>
          <MenuItem>
            <NavLink to="/contact" active={location.pathname === '/contact'}>Contact</NavLink>
          </MenuItem>
        </MenuItems>
      </Nav>
    </HeaderContainer>
  );
};

export default Header;
